import {useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useParams} from "react-router";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {WideContent} from "@pg-design/grid-module";
import {HorizontalListIcon, InfoIcon, SettingsIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {fromQueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty, omit} from "@pg-mono/nodash";
import {appendQueryString, IQuery, parseSearch} from "@pg-mono/request";
import {rpAppLink, rpAppPath} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";
import {navigationHeight} from "@pg-rp/navigation";

import {IRPStore} from "../../app/rp_reducer";
import {LeadModal} from "../../application/components/LeadModal";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {useHitUserSegmentLocationEntryOnce} from "../../hooks/use_hit_user_segment_location_entry_once";
import {isRailTransportAvailable} from "../../maps/utils/is_rail_transport_available";
import {useCookieBotObserver} from "../../navigation/hooks/use_cookie_bot_observer";
import {Country} from "../../region/types/Country";
import {SlotOrderType} from "../../search/components/search_autocomplete/SearchAutocompleteInputWithSlot";
import {SearchFormWithDrawer} from "../../search/components/SearchFormWithDrawer";
import {searchFormWithDrawerHeight} from "../../search/constants/search_form_with_drawer_height";
import {useSearchForm} from "../../search/hooks/use_search_form";
import {ISearchFiltersFormValues} from "../../search/types/ISearchFiltersFormValues";
import {openSidebarHit, sidebarCloseHit} from "../../tracking/algolytics_hits/map_interaction_hits";
import {ISearchRegion, offerListSearchHit, prepareOfferListSearchData} from "../../tracking/algolytics_hits/offer_list_search_hit";
import {IAmplitudeViewItemOffer} from "../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackOfferSelectItemWithListContext} from "../../tracking/amplitude/track_offer_select_item_with_list_context";
import {gtmEventOffersCount} from "../../tracking/google_tag_manager/gtm_event_ui";
import {Currency} from "../../types/Currency";
import {parseQueryParamToInt} from "../../utils/latest_query";
import {ViewType} from "../../view_type/ViewType";
import {MapSize} from "../atoms/MapSize";
import {MapOfferPreview} from "../components/map_offer/MapOfferPreview";
import {OfferListMap} from "../components/OfferListMap";
import {ExpandablePanelMapHeader} from "../components/os_map_for_offers/ExpandablePanelMapHeader";
import {ExpandablePanelMapLayers} from "../components/os_map_for_offers/ExpandablePanelMapLayers";
import {fetchOfferDetailProperties, PropertyListTabTypes} from "../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../detail/components/property_list/OfferDetailPropertyListTable";
import {getOfferListSubFilterParsed} from "../helpers/friendly_offer_list/get_offer_list_sub_filter_parsed";
import {OfferDisplayType} from "../helpers/OfferDisplayType";
import {useMapOfferListData} from "../hooks/use_map_offer_list_data";
import {useMapOfferListQuery} from "../hooks/use_map_offer_list_query";
import {useOfferListMapTransportRoutes} from "../hooks/use_offer_list_map_transport_routes";
import {useSeenOfferIds} from "../hooks/use_seen_offer_ids";
import {ISelectedRegion} from "../list/actions/offer_list_selected_data_actions";
import {DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE} from "../list/components/OfferListPropertiesModal";
import {getOfferListingAmplitudeItemListContext} from "../list/helpers/get_offer_listing_amplitude_item_list_context";
import {getCountryId} from "../list/helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {IOfferListLatestQuery} from "../list/types/IOfferListLatestQuery";
import {hasPriceFilter} from "../list/utils/has_price_filter";
import {useGetMapRegionsQuery} from "../map/api/get_map_region";
import {MapGeneratedDescription} from "../map/components/MapGeneratedDescription";
import {MapHeader} from "../map/components/MapHeader";
import {gtmEventMapList, MapGTMActions, MapGTMLabel} from "../map/utils/map_analytics";
import {hitGTMOfferSelectItem} from "../tracking/ecommerce_events/offer/hit_gtm_offer_select_item";
import {IOfferListingParams} from "../types/OfferListingParams";
import {OfferType} from "../types/OfferType";

export const OfferListMapView = () => {
    const {isMobile} = useUserDevice();
    const [isHoliday, setIsHoliday] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [offerId, setOfferId] = useState<number | null>(null);
    const {seenOfferIds, addSeenOfferId} = useSeenOfferIds();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showGeneratedDescription, setShowGeneratedDescription] = useState(false);
    const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
    const [isPricePanelOpen, setIsPricePanelOpen] = useState(false);
    const location = useLocation();
    const routeParams = useParams<IOfferListingParams>();

    const selectedRegion = useSelector((state: IRPStore) => state.offerListMap.selectedRegion);
    const regionTypes = useSelector((state: IRPStore) => (state.variables.data ? state.variables.data.region_type : []));
    const pageTitle = useSelector((state: IRPStore) => state.metaData.standardMetaData.title);
    const travelTime = useSelector((state: IRPStore) => state.search.travelTime);
    const locationSearchTab = useSelector((state: IRPStore) => state.search.searchForm.locationSearchTab);

    const {transportRoutes} = useOfferListMapTransportRoutes();
    const hasRailTransport = isRailTransportAvailable(transportRoutes);
    const mapOfferListQuery = useMapOfferListQuery();
    const {offers, count, propertiesCount, isLoading, isError, latestQuery} = useMapOfferListData({
        mapOfferListQuery: mapOfferListQuery
    });

    const priceFilterActive = hasPriceFilter(latestQuery as IOfferListLatestQuery);
    const displayOffersCount = priceFilterActive ? (offers?.filter((offer) => offer.has_required_prices === true).length ?? 0) : count;

    const {isCookieBotOpen} = useCookieBotObserver();

    // Map regions data
    const {data: offerListRegion} = useGetMapRegionsQuery({region: selectedRegion.map((region) => region.id)});

    const trackingMeta = {
        listName: pageTitle,
        listId: "mapa"
    };
    const listingTitle = pageTitle.split(" - ")[0];
    const amplitudeItemListContext = useMemo(
        () =>
            getOfferListingAmplitudeItemListContext({
                listingTitle,
                isPromotionsTab: false,
                hasPromotionTabs: false,
                latestQuery: latestQuery as IOfferListLatestQuery
            }),
        [listingTitle, latestQuery]
    );

    const generatedDescriptionAllowed =
        offerListRegion?.results.length === 1 &&
        !!latestQuery.type &&
        parseQueryParamToInt(latestQuery.type) === OfferType.FLAT &&
        isEmpty(omit(latestQuery, ["type", "region"]));

    const headerPropertiesCount = isInitialized ? propertiesCount : null;
    const headerOffersCount = isInitialized ? displayOffersCount : 0;
    const headerGeneratedDescriptionAllowed = isInitialized && generatedDescriptionAllowed;

    /**
     * Lifecycle
     */
    useEffect(() => {
        const query = parseSearch(location.search);
        const isHoliday = query.is_holiday === "1";
        setIsHoliday(isHoliday);
    }, []);

    useEffect(() => {
        if (isLoading || isInitialized) {
            return;
        }

        setIsInitialized(true);
    }, [isLoading]);

    // Trigger search hit when offers are loaded
    useEffect(() => {
        if (!isLoading && offers && offers?.length > 0) {
            hitSearch();
        }
    }, [isLoading, offers?.length]);

    useHitUserSegmentLocationEntryOnce({
        enabled: isInitialized && selectedRegion.length > 0,
        regions: selectedRegion
    });

    // Top Offset of the blend under search fields
    const blendHeightForFiltersWithDrawer = `${navigationHeight + searchFormWithDrawerHeight}px`;

    /**
     * Sidebar implementation
     */
    const dispatch = useDispatch();
    const latestSearchFilters = fromQueryValues<ISearchFiltersFormValues>(omit(propertyListTableFiltersFields, ["price"]), latestQuery);

    /**
     * Callbacks
     */

    const {openLocationSearch} = useSearchForm();
    const handleMapHeaderClick = () => {
        openLocationSearch();
    };

    // map will flicker without useCallback
    const onMarkerClick = useCallback(
        (offerId: number) => {
            const offer = offers?.find((offer) => offer.id === offerId);
            const offerIndex = offers?.findIndex((offer) => offer.id === offerId) ?? 0;

            if (!offer) {
                notifyBugsnag(
                    {
                        name: "NonExistentInvestmentSelected",
                        message: `User clicked on non-existent investment with offerId: ${offerId}`
                    },
                    "onMarkerClick - selected non-existent investment"
                );
                return;
            }

            hitGTMOfferSelectItem({
                offer: {
                    ...offer,
                    configuration: {
                        ...offer.configuration,
                        // The following properties are not in the offer, but there are in the API query
                        limited_presentation: false,
                        display_type: OfferDisplayType.FOR_SALE
                    }
                },
                listId: trackingMeta.listId,
                listName: trackingMeta.listName
            });
            trackOfferSelectItemWithListContext({
                offer: {
                    id: offer.id,
                    name: offer.name,
                    type: offer.type as IAmplitudeViewItemOffer["type"],
                    currency: offer.region.country === Country.SPAIN ? Currency.EURO : Currency.PLN,
                    vendor: offer.vendor,
                    region: offer.region as IAmplitudeViewItemOffer["region"]
                },
                index: Math.max(offerIndex, 0),
                itemListContext: amplitudeItemListContext
            });
            setIsSidebarOpen(true);
            gtmEventMapList(MapGTMActions.PIN, offerId);
            openSidebarHit(ViewType.OFFER_LIST_MAP, offerId);
            setOfferId(offerId);
            addSeenOfferId(offerId);
        },
        [amplitudeItemListContext, offers, trackingMeta.listId, trackingMeta.listName]
    );

    // Sidebar implementation
    const onOfferSidebarClose = () => {
        setIsSidebarOpen(false);
        gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.CLOSE);
        sidebarCloseHit(ViewType.OFFER_LIST_MAP);
        setOfferId(null);
    };

    const handleSetOfferId = (offerId: number | null) => {
        setOfferId(offerId);
        if (offerId !== null) {
            addSeenOfferId(offerId);
        }
    };

    const getGoBackToListUrl = () => {
        const parsedSearch = parseSearch(location.search);
        const {_page, _sort, ...validSearch} = parsedSearch;

        const {friendlySlug, offerListSubFilter, offerListSubType, abroadRegionSlug, country} = routeParams;

        if (friendlySlug && country && getCountryId(country) !== Country.POLAND) {
            return appendQueryString(
                rpAppLink.offer.listFriendlyAbroad({
                    friendlySlug,
                    country,
                    abroadRegionSlug
                }),
                validSearch
            );
        }

        if (friendlySlug && offerListSubFilter) {
            const subFilterQuery = getOfferListSubFilterParsed(offerListSubFilter);
            return appendQueryString(rpAppLink.offer.listFriendly({friendlySlug}), {
                ...validSearch,
                ...subFilterQuery
            });
        }

        if (friendlySlug && offerListSubType) {
            return appendQueryString(
                rpAppLink.offer.listFriendlySubType({
                    friendlySlug,
                    offerListSubType
                }),
                validSearch
            );
        }

        if (friendlySlug) {
            return appendQueryString(rpAppLink.offer.listFriendly({friendlySlug}), validSearch);
        }

        if (location.pathname === rpAppPath.multiOfferList.map) {
            const url = rpAppLink.offer.multiList();
            return appendQueryString(url, latestQuery as IQuery);
        }

        return rpAppLink.offer.list();
    };

    const hitSearch = () => {
        const searchRegions: ISearchRegion[] = selectedRegion.reduce((acc: ISearchRegion[], region: ISelectedRegion) => {
            const regionType = region && regionTypes?.find((t) => t.value === region.type);
            return [...acc, {...region, type__display: regionType ? regionType.label.toLocaleLowerCase() : ""}];
        }, []);

        const searchData = prepareOfferListSearchData({
            offers_count: displayOffersCount,
            properties_count: propertiesCount || 0,
            latestQuery,
            searchRegions,
            query: parseSearch(location.search),
            travel_time: travelTime,
            locationSearchTab
        });

        const offerIds: number[] = [];
        const offerNames: string[] = [];
        const vendorIds: number[] = [];
        const vendorNames: string[] = [];
        offers?.forEach((offer) => {
            offerIds.push(offer && offer.id);
            offerNames.push(offer && offer.name);
            vendorIds.push(offer.vendor && offer.vendor.id);
            vendorNames.push(offer.vendor && offer.vendor.name);
        });

        // When sidebar is open and filters are changing update properties list
        if (isSidebarOpen && offerId) {
            fetchOfferDetailProperties(
                offerId,
                PropertyListTabTypes.MODAL,
                DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE,
                {page: 1},
                {...toQueryValues(propertyListTableFiltersFields, latestSearchFilters)}
            )(dispatch);
        }

        // gtm
        gtmEventOffersCount(displayOffersCount);
        // algolytics
        offerListSearchHit(ViewType.OFFER_LIST_MAP, searchData, offerIds, offerNames, vendorIds, vendorNames);
        // FacebookPixel.track("Search");
    };

    const handleListButtonClick = () => gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.LISTING);

    const generatedDescription = offerListRegion?.results.length ? (
        <MapGeneratedDescription
            generatedDescriptionAllowed={generatedDescriptionAllowed}
            onHideGenericDescription={() => setShowGeneratedDescription(false)}
            region={offerListRegion?.results[0]}
            showGeneratedDescription={showGeneratedDescription}
        />
    ) : null;

    /**
     * Render
     */

    return (
        <div className={css({position: "relative"})}>
            <div className={offerListMapWrapper}>
                <div className={searchWrapper}>
                    {isMobile && (
                        <ExpandablePanelMapHeader
                            offersCount={headerOffersCount}
                            propertiesCount={headerPropertiesCount}
                            isOpen={isInfoPanelOpen}
                            generatedDescriptionAllowed={headerGeneratedDescriptionAllowed}
                            handleClose={() => setIsInfoPanelOpen(false)}
                            onShowGeneratedDescription={() => setShowGeneratedDescription(true)}
                        />
                    )}
                    <ExpandablePanelMapLayers transformRoutes={transportRoutes} isOpen={isPricePanelOpen} handleClose={() => setIsPricePanelOpen(false)} />
                    <WideContent>
                        <SearchFormWithDrawer
                            listFriendlyLink={rpAppLink.offer.mapFriendly}
                            abroadListFriendlyLink={({
                                friendlySlug,
                                country,
                                abroadRegionSlug
                            }: {
                                friendlySlug: string;
                                country: string;
                                abroadRegionSlug?: string;
                            }) => {
                                return abroadRegionSlug
                                    ? rpAppLink.offer.mapAbroadRegion({
                                          friendlySlug,
                                          country,
                                          abroadRegionSlug
                                      })
                                    : rpAppLink.offer.mapAbroadCountry({friendlySlug, country});
                            }}
                            multiRegionLink={rpAppLink.offer.multiListMap}
                            latestQuery={latestQuery}
                            hasParentBeenInitialized={isInitialized}
                            sortActive={false}
                            // Blend will start on the bottom line of the Search component
                            // TODO: Sometimes blend is relative to their elements, navigationHeight should be removed from atoms/DesktopBlend implementation
                            blendTop={`${searchFormWithDrawerHeight - navigationHeight}px`}
                            blendHeightDecrease={blendHeightForFiltersWithDrawer}
                            isMap
                            showMobileTopSearchTrigger={count > 0}
                            slotOrder={SlotOrderType.ROW_REVERSE}
                            showTopFilters
                            className={css({mb: 0, zIndex: 2})}
                            disableLocationSearch={isCookieBotOpen}
                        />
                    </WideContent>
                </div>

                <div className={mapHolder}>
                    {hasRailTransport && (
                        <button
                            className={cx(sideButton, css({top: 3}))}
                            onClick={() => {
                                setIsPricePanelOpen((prev) => !prev);
                                setIsInfoPanelOpen(false);
                            }}
                        >
                            <SettingsIcon
                                size="2.4"
                                wrapperColor="transparent"
                                wrapperSize="2.4"
                                fill={isPricePanelOpen ? "var(--colors-gray-600)" : "var(--colors-secondary)"}
                            />
                        </button>
                    )}
                    <button
                        className={cx(sideButton, css({top: "8.8rem"}))}
                        onClick={() => {
                            setIsInfoPanelOpen((prev) => !prev);
                            setIsPricePanelOpen(false);
                        }}
                    >
                        <InfoIcon
                            size="2.4"
                            wrapperColor="transparent"
                            wrapperSize="2.4"
                            fill={isPricePanelOpen ? "var(--colors-gray-600)" : "var(--colors-secondary)"}
                        />
                    </button>
                    {isInitialized ? (
                        <MapSize isSideBarOpen={isSidebarOpen}>
                            <OfferListMap
                                transportRoutes={transportRoutes}
                                onMarkerClick={onMarkerClick}
                                offers={offers}
                                isLoading={isLoading}
                                isError={isError}
                                isHoliday={isHoliday}
                                seenOfferIds={seenOfferIds}
                            />
                        </MapSize>
                    ) : (
                        <div className={mapLoaderHolder}>
                            <Loader size="lg" />
                        </div>
                    )}

                    <>
                        <Button
                            href={getGoBackToListUrl()}
                            onClick={handleListButtonClick}
                            iconLeft={HorizontalListIcon}
                            className={listButton}
                            variant="filled_primary"
                        >
                            Lista
                        </Button>
                        {!isMobile && (
                            <MapHeader
                                pageTitle={pageTitle}
                                offerCount={headerOffersCount}
                                propertiesCount={headerPropertiesCount}
                                selectedRegion={selectedRegion}
                                generatedDescriptionAllowed={headerGeneratedDescriptionAllowed}
                                onShowGeneratedDescription={() => setShowGeneratedDescription(true)}
                                onHeaderClick={handleMapHeaderClick}
                            />
                        )}
                    </>
                    {offerId && (
                        <MapOfferPreview
                            offerId={offerId}
                            offerIndex={Math.max(offers?.findIndex((offer) => offer.id === offerId) ?? 0, 0)}
                            handleSetOfferId={handleSetOfferId}
                            onClose={onOfferSidebarClose}
                            latestQuery={latestQuery}
                            mapOfferListQuery={mapOfferListQuery}
                            isOpen={isSidebarOpen}
                            amplitudeItemListContext={amplitudeItemListContext}
                        />
                    )}

                    {generatedDescription}
                </div>
            </div>
            <LeadModal />
        </div>
    );
};

const mapHolder = css({
    position: "relative",
    display: "flex",
    flexDirection: "row",
    flex: 1,
    overflowX: "hidden"
});

const mapLoaderHolder = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1
});

const listButton = css({
    display: "none",

    md: {
        minWidth: "15.6rem",
        pl: 5,
        pr: 5,
        display: "block",
        position: "absolute",
        top: 3,
        left: 3
    }
});

const offerListMapWrapper = css({
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 68px)",
    width: "100%"
});

const searchWrapper = css({
    display: "block",
    width: "100%",
    height: "auto",
    backgroundColor: "var(--colors-white)",
    pb: 3,
    zIndex: 2,
    boxShadow: "var(--shadow-4)",

    md: {
        backgroundColor: "initial",
        pb: 0,
        boxShadow: "var(--shadow-0)"
    }
});

const sideButton = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "0.5rem 0 0 0.5rem",
    position: "absolute",
    p: 1,
    right: 0,
    backgroundColor: "var(--colors-white)",
    zIndex: 1,

    md: {
        display: "none"
    }
});
import {useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useParams} from "react-router";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {WideContent} from "@pg-design/grid-module";
import {HorizontalListIcon, InfoIcon, SettingsIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {fromQueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty, omit} from "@pg-mono/nodash";
import {appendQueryString, IQuery, parseSearch} from "@pg-mono/request";
import {rpAppLink, rpAppPath} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";
import {navigationHeight} from "@pg-rp/navigation";

import {IRPStore} from "../../app/rp_reducer";
import {LeadModal} from "../../application/components/LeadModal";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {useHitUserSegmentLocationEntryOnce} from "../../hooks/use_hit_user_segment_location_entry_once";
import {isRailTransportAvailable} from "../../maps/utils/is_rail_transport_available";
import {useCookieBotObserver} from "../../navigation/hooks/use_cookie_bot_observer";
import {Country} from "../../region/types/Country";
import {SlotOrderType} from "../../search/components/search_autocomplete/SearchAutocompleteInputWithSlot";
import {SearchFormWithDrawer} from "../../search/components/SearchFormWithDrawer";
import {searchFormWithDrawerHeight} from "../../search/constants/search_form_with_drawer_height";
import {useSearchForm} from "../../search/hooks/use_search_form";
import {ISearchFiltersFormValues} from "../../search/types/ISearchFiltersFormValues";
import {openSidebarHit, sidebarCloseHit} from "../../tracking/algolytics_hits/map_interaction_hits";
import {ISearchRegion, offerListSearchHit, prepareOfferListSearchData} from "../../tracking/algolytics_hits/offer_list_search_hit";
import {IAmplitudeViewItemOffer} from "../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackOfferSelectItemWithListContext} from "../../tracking/amplitude/track_offer_select_item_with_list_context";
import {gtmEventOffersCount} from "../../tracking/google_tag_manager/gtm_event_ui";
import {Currency} from "../../types/Currency";
import {parseQueryParamToInt} from "../../utils/latest_query";
import {ViewType} from "../../view_type/ViewType";
import {MapSize} from "../atoms/MapSize";
import {MapOfferPreview} from "../components/map_offer/MapOfferPreview";
import {OfferListMap} from "../components/OfferListMap";
import {ExpandablePanelMapHeader} from "../components/os_map_for_offers/ExpandablePanelMapHeader";
import {ExpandablePanelMapLayers} from "../components/os_map_for_offers/ExpandablePanelMapLayers";
import {fetchOfferDetailProperties, PropertyListTabTypes} from "../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../detail/components/property_list/OfferDetailPropertyListTable";
import {getOfferListSubFilterParsed} from "../helpers/friendly_offer_list/get_offer_list_sub_filter_parsed";
import {OfferDisplayType} from "../helpers/OfferDisplayType";
import {useMapOfferListData} from "../hooks/use_map_offer_list_data";
import {useMapOfferListQuery} from "../hooks/use_map_offer_list_query";
import {useOfferListMapTransportRoutes} from "../hooks/use_offer_list_map_transport_routes";
import {useSeenOfferIds} from "../hooks/use_seen_offer_ids";
import {ISelectedRegion} from "../list/actions/offer_list_selected_data_actions";
import {DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE} from "../list/components/OfferListPropertiesModal";
import {getOfferListingAmplitudeItemListContext} from "../list/helpers/get_offer_listing_amplitude_item_list_context";
import {getCountryId} from "../list/helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {IOfferListLatestQuery} from "../list/types/IOfferListLatestQuery";
import {hasPriceFilter} from "../list/utils/has_price_filter";
import {useGetMapRegionsQuery} from "../map/api/get_map_region";
import {MapGeneratedDescription} from "../map/components/MapGeneratedDescription";
import {MapHeader} from "../map/components/MapHeader";
import {gtmEventMapList, MapGTMActions, MapGTMLabel} from "../map/utils/map_analytics";
import {hitGTMOfferSelectItem} from "../tracking/ecommerce_events/offer/hit_gtm_offer_select_item";
import {IOfferListingParams} from "../types/OfferListingParams";
import {OfferType} from "../types/OfferType";

export const OfferListMapView = () => {
    const {isMobile} = useUserDevice();
    const [isHoliday, setIsHoliday] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [offerId, setOfferId] = useState<number | null>(null);
    const {seenOfferIds, addSeenOfferId} = useSeenOfferIds();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showGeneratedDescription, setShowGeneratedDescription] = useState(false);
    const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
    const [isPricePanelOpen, setIsPricePanelOpen] = useState(false);
    const location = useLocation();
    const routeParams = useParams<IOfferListingParams>();

    const selectedRegion = useSelector((state: IRPStore) => state.offerListMap.selectedRegion);
    const regionTypes = useSelector((state: IRPStore) => (state.variables.data ? state.variables.data.region_type : []));
    const pageTitle = useSelector((state: IRPStore) => state.metaData.standardMetaData.title);
    const travelTime = useSelector((state: IRPStore) => state.search.travelTime);
    const locationSearchTab = useSelector((state: IRPStore) => state.search.searchForm.locationSearchTab);

    const {transportRoutes} = useOfferListMapTransportRoutes();
    const hasRailTransport = isRailTransportAvailable(transportRoutes);
    const mapOfferListQuery = useMapOfferListQuery();
    const {offers, count, propertiesCount, isLoading, isError, latestQuery} = useMapOfferListData({
        mapOfferListQuery: mapOfferListQuery
    });

    const priceFilterActive = hasPriceFilter(latestQuery as IOfferListLatestQuery);
    const displayOffersCount = priceFilterActive ? (offers?.filter((offer) => offer.has_required_prices === true).length ?? 0) : count;

    const {isCookieBotOpen} = useCookieBotObserver();

    // Map regions data
    const {data: offerListRegion} = useGetMapRegionsQuery({region: selectedRegion.map((region) => region.id)});

    const trackingMeta = {
        listName: pageTitle,
        listId: "mapa"
    };
    const listingTitle = pageTitle.split(" - ")[0];
    const amplitudeItemListContext = useMemo(
        () =>
            getOfferListingAmplitudeItemListContext({
                listingTitle,
                isPromotionsTab: false,
                hasPromotionTabs: false,
                latestQuery: latestQuery as IOfferListLatestQuery
            }),
        [listingTitle, latestQuery]
    );

    const generatedDescriptionAllowed =
        offerListRegion?.results.length === 1 &&
        !!latestQuery.type &&
        parseQueryParamToInt(latestQuery.type) === OfferType.FLAT &&
        isEmpty(omit(latestQuery, ["type", "region"]));

    const headerPropertiesCount = isInitialized ? propertiesCount : null;
    const headerOffersCount = isInitialized ? displayOffersCount : 0;
    const headerGeneratedDescriptionAllowed = isInitialized && generatedDescriptionAllowed;

    /**
     * Lifecycle
     */
    useEffect(() => {
        const query = parseSearch(location.search);
        const isHoliday = query.is_holiday === "1";
        setIsHoliday(isHoliday);
    }, []);

    useEffect(() => {
        if (isLoading || isInitialized) {
            return;
        }

        setIsInitialized(true);
    }, [isLoading]);

    // Trigger search hit when offers are loaded
    useEffect(() => {
        if (!isLoading && offers && offers?.length > 0) {
            hitSearch();
        }
    }, [isLoading, offers?.length]);

    useHitUserSegmentLocationEntryOnce({
        enabled: isInitialized && selectedRegion.length > 0,
        regions: selectedRegion
    });

    // Top Offset of the blend under search fields
    const blendHeightForFiltersWithDrawer = `${navigationHeight + searchFormWithDrawerHeight}px`;

    /**
     * Sidebar implementation
     */
    const dispatch = useDispatch();
    const latestSearchFilters = fromQueryValues<ISearchFiltersFormValues>(omit(propertyListTableFiltersFields, ["price"]), latestQuery);

    /**
     * Callbacks
     */

    const {openLocationSearch} = useSearchForm();
    const handleMapHeaderClick = () => {
        openLocationSearch();
    };

    // map will flicker without useCallback
    const onMarkerClick = useCallback(
        (offerId: number) => {
            const offer = offers?.find((offer) => offer.id === offerId);
            const offerIndex = offers?.findIndex((offer) => offer.id === offerId) ?? 0;

            if (!offer) {
                notifyBugsnag(
                    {
                        name: "NonExistentInvestmentSelected",
                        message: `User clicked on non-existent investment with offerId: ${offerId}`
                    },
                    "onMarkerClick - selected non-existent investment"
                );
                return;
            }

            hitGTMOfferSelectItem({
                offer: {
                    ...offer,
                    configuration: {
                        ...offer.configuration,
                        // The following properties are not in the offer, but there are in the API query
                        limited_presentation: false,
                        display_type: OfferDisplayType.FOR_SALE
                    }
                },
                listId: trackingMeta.listId,
                listName: trackingMeta.listName
            });
            trackOfferSelectItemWithListContext({
                offer: {
                    id: offer.id,
                    name: offer.name,
                    type: offer.type as IAmplitudeViewItemOffer["type"],
                    currency: offer.region.country === Country.SPAIN ? Currency.EURO : Currency.PLN,
                    vendor: offer.vendor,
                    region: offer.region as IAmplitudeViewItemOffer["region"]
                },
                index: Math.max(offerIndex, 0),
                itemListContext: amplitudeItemListContext
            });
            setIsSidebarOpen(true);
            gtmEventMapList(MapGTMActions.PIN, offerId);
            openSidebarHit(ViewType.OFFER_LIST_MAP, offerId);
            setOfferId(offerId);
            addSeenOfferId(offerId);
        },
        [amplitudeItemListContext, offers, trackingMeta.listId, trackingMeta.listName]
    );

    // Sidebar implementation
    const onOfferSidebarClose = () => {
        setIsSidebarOpen(false);
        gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.CLOSE);
        sidebarCloseHit(ViewType.OFFER_LIST_MAP);
        setOfferId(null);
    };

    const handleSetOfferId = (offerId: number | null) => {
        setOfferId(offerId);
        if (offerId !== null) {
            addSeenOfferId(offerId);
        }
    };

    const getGoBackToListUrl = () => {
        const parsedSearch = parseSearch(location.search);
        const {_page, _sort, ...validSearch} = parsedSearch;

        const {friendlySlug, offerListSubFilter, offerListSubType, abroadRegionSlug, country} = routeParams;

        if (friendlySlug && country && getCountryId(country) !== Country.POLAND) {
            return appendQueryString(
                rpAppLink.offer.listFriendlyAbroad({
                    friendlySlug,
                    country,
                    abroadRegionSlug
                }),
                validSearch
            );
        }

        if (friendlySlug && offerListSubFilter) {
            const subFilterQuery = getOfferListSubFilterParsed(offerListSubFilter);
            return appendQueryString(rpAppLink.offer.listFriendly({friendlySlug}), {
                ...validSearch,
                ...subFilterQuery
            });
        }

        if (friendlySlug && offerListSubType) {
            return appendQueryString(
                rpAppLink.offer.listFriendlySubType({
                    friendlySlug,
                    offerListSubType
                }),
                validSearch
            );
        }

        if (friendlySlug) {
            return appendQueryString(rpAppLink.offer.listFriendly({friendlySlug}), validSearch);
        }

        if (location.pathname === rpAppPath.multiOfferList.map) {
            const url = rpAppLink.offer.multiList();
            return appendQueryString(url, latestQuery as IQuery);
        }

        return rpAppLink.offer.list();
    };

    const hitSearch = () => {
        const searchRegions: ISearchRegion[] = selectedRegion.reduce((acc: ISearchRegion[], region: ISelectedRegion) => {
            const regionType = region && regionTypes?.find((t) => t.value === region.type);
            return [...acc, {...region, type__display: regionType ? regionType.label.toLocaleLowerCase() : ""}];
        }, []);

        const searchData = prepareOfferListSearchData({
            offers_count: displayOffersCount,
            properties_count: propertiesCount || 0,
            latestQuery,
            searchRegions,
            query: parseSearch(location.search),
            travel_time: travelTime,
            locationSearchTab
        });

        const offerIds: number[] = [];
        const offerNames: string[] = [];
        const vendorIds: number[] = [];
        const vendorNames: string[] = [];
        offers?.forEach((offer) => {
            offerIds.push(offer && offer.id);
            offerNames.push(offer && offer.name);
            vendorIds.push(offer.vendor && offer.vendor.id);
            vendorNames.push(offer.vendor && offer.vendor.name);
        });

        // When sidebar is open and filters are changing update properties list
        if (isSidebarOpen && offerId) {
            fetchOfferDetailProperties(
                offerId,
                PropertyListTabTypes.MODAL,
                DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE,
                {page: 1},
                {...toQueryValues(propertyListTableFiltersFields, latestSearchFilters)}
            )(dispatch);
        }

        // gtm
        gtmEventOffersCount(displayOffersCount);
        // algolytics
        offerListSearchHit(ViewType.OFFER_LIST_MAP, searchData, offerIds, offerNames, vendorIds, vendorNames);
        // FacebookPixel.track("Search");
    };

    const handleListButtonClick = () => gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.LISTING);

    const generatedDescription = offerListRegion?.results.length ? (
        <MapGeneratedDescription
            generatedDescriptionAllowed={generatedDescriptionAllowed}
            onHideGenericDescription={() => setShowGeneratedDescription(false)}
            region={offerListRegion?.results[0]}
            showGeneratedDescription={showGeneratedDescription}
        />
    ) : null;

    /**
     * Render
     */

    return (
        <div className={css({position: "relative"})}>
            <div className={offerListMapWrapper}>
                <div className={searchWrapper}>
                    {isMobile && (
                        <ExpandablePanelMapHeader
                            offersCount={headerOffersCount}
                            propertiesCount={headerPropertiesCount}
                            isOpen={isInfoPanelOpen}
                            generatedDescriptionAllowed={headerGeneratedDescriptionAllowed}
                            handleClose={() => setIsInfoPanelOpen(false)}
                            onShowGeneratedDescription={() => setShowGeneratedDescription(true)}
                        />
                    )}
                    <ExpandablePanelMapLayers transformRoutes={transportRoutes} isOpen={isPricePanelOpen} handleClose={() => setIsPricePanelOpen(false)} />
                    <WideContent>
                        <SearchFormWithDrawer
                            listFriendlyLink={rpAppLink.offer.mapFriendly}
                            abroadListFriendlyLink={({
                                friendlySlug,
                                country,
                                abroadRegionSlug
                            }: {
                                friendlySlug: string;
                                country: string;
                                abroadRegionSlug?: string;
                            }) => {
                                return abroadRegionSlug
                                    ? rpAppLink.offer.mapAbroadRegion({
                                          friendlySlug,
                                          country,
                                          abroadRegionSlug
                                      })
                                    : rpAppLink.offer.mapAbroadCountry({friendlySlug, country});
                            }}
                            multiRegionLink={rpAppLink.offer.multiListMap}
                            latestQuery={latestQuery}
                            hasParentBeenInitialized={isInitialized}
                            sortActive={false}
                            // Blend will start on the bottom line of the Search component
                            // TODO: Sometimes blend is relative to their elements, navigationHeight should be removed from atoms/DesktopBlend implementation
                            blendTop={`${searchFormWithDrawerHeight - navigationHeight}px`}
                            blendHeightDecrease={blendHeightForFiltersWithDrawer}
                            isMap
                            showMobileTopSearchTrigger={count > 0}
                            slotOrder={SlotOrderType.ROW_REVERSE}
                            showTopFilters
                            className={css({mb: 0, zIndex: 2})}
                            disableLocationSearch={isCookieBotOpen}
                        />
                    </WideContent>
                </div>

                <div className={mapHolder}>
                    {hasRailTransport && (
                        <button
                            className={cx(sideButton, css({top: 3}))}
                            onClick={() => {
                                setIsPricePanelOpen((prev) => !prev);
                                setIsInfoPanelOpen(false);
                            }}
                        >
                            <SettingsIcon
                                size="2.4"
                                wrapperColor="transparent"
                                wrapperSize="2.4"
                                fill={isPricePanelOpen ? "var(--colors-gray-600)" : "var(--colors-secondary)"}
                            />
                        </button>
                    )}
                    <button
                        className={cx(sideButton, css({top: "8.8rem"}))}
                        onClick={() => {
                            setIsInfoPanelOpen((prev) => !prev);
                            setIsPricePanelOpen(false);
                        }}
                    >
                        <InfoIcon
                            size="2.4"
                            wrapperColor="transparent"
                            wrapperSize="2.4"
                            fill={isPricePanelOpen ? "var(--colors-gray-600)" : "var(--colors-secondary)"}
                        />
                    </button>
                    {isInitialized ? (
                        <MapSize isSideBarOpen={isSidebarOpen}>
                            <OfferListMap
                                transportRoutes={transportRoutes}
                                onMarkerClick={onMarkerClick}
                                offers={offers}
                                isLoading={isLoading}
                                isError={isError}
                                isHoliday={isHoliday}
                                seenOfferIds={seenOfferIds}
                            />
                        </MapSize>
                    ) : (
                        <div className={mapLoaderHolder}>
                            <Loader size="lg" />
                        </div>
                    )}

                    <>
                        <Button
                            href={getGoBackToListUrl()}
                            onClick={handleListButtonClick}
                            iconLeft={HorizontalListIcon}
                            className={listButton}
                            variant="filled_primary"
                        >
                            Lista
                        </Button>
                        {!isMobile && (
                            <MapHeader
                                pageTitle={pageTitle}
                                offerCount={headerOffersCount}
                                propertiesCount={headerPropertiesCount}
                                selectedRegion={selectedRegion}
                                generatedDescriptionAllowed={headerGeneratedDescriptionAllowed}
                                onShowGeneratedDescription={() => setShowGeneratedDescription(true)}
                                onHeaderClick={handleMapHeaderClick}
                            />
                        )}
                    </>
                    {offerId && (
                        <MapOfferPreview
                            offerId={offerId}
                            offerIndex={Math.max(offers?.findIndex((offer) => offer.id === offerId) ?? 0, 0)}
                            handleSetOfferId={handleSetOfferId}
                            onClose={onOfferSidebarClose}
                            latestQuery={latestQuery}
                            mapOfferListQuery={mapOfferListQuery}
                            isOpen={isSidebarOpen}
                            amplitudeItemListContext={amplitudeItemListContext}
                        />
                    )}

                    {generatedDescription}
                </div>
            </div>
            <LeadModal />
        </div>
    );
};

const mapHolder = css({
    position: "relative",
    display: "flex",
    flexDirection: "row",
    flex: 1,
    overflowX: "hidden"
});

const mapLoaderHolder = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1
});

const listButton = css({
    display: "none",

    md: {
        minWidth: "15.6rem",
        pl: 5,
        pr: 5,
        display: "block",
        position: "absolute",
        top: 3,
        left: 3
    }
});

const offerListMapWrapper = css({
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 68px)",
    width: "100%"
});

const searchWrapper = css({
    display: "block",
    width: "100%",
    height: "auto",
    backgroundColor: "var(--colors-white)",
    pb: 3,
    zIndex: 2,
    boxShadow: "var(--shadow-4)",

    md: {
        backgroundColor: "initial",
        pb: 0,
        boxShadow: "var(--shadow-0)"
    }
});

const sideButton = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "0.5rem 0 0 0.5rem",
    position: "absolute",
    p: 1,
    right: 0,
    backgroundColor: "var(--colors-white)",
    zIndex: 1,

    md: {
        display: "none"
    }
});
